import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Home, Car, CreditCard, Briefcase, Lock, CheckCircle, Lightbulb } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { ROUTE_PATHS } from '@/lib/index';
import { houseGoalPhases } from '@/lib/dashboard-data';
import { springPresets } from '@/lib/motion';

export default function GoalRoadmap() {
  const [selectedGoal, setSelectedGoal] = useState<'house' | 'car' | 'card' | 'funding'>('house');
  const [completedTasks, setCompletedTasks] = useState<string[]>(['1-1', '1-2']);

  const goals = [
    { id: 'house' as const, icon: Home, label: 'Buy a House', color: 'text-primary', bgColor: 'bg-primary/10' },
    { id: 'car' as const, icon: Car, label: 'Buy a Car', color: 'text-accent', bgColor: 'bg-accent/10' },
    { id: 'card' as const, icon: CreditCard, label: 'Get Credit Card', color: 'text-chart-3', bgColor: 'bg-chart-3/10' },
    { id: 'funding' as const, icon: Briefcase, label: 'Business Funding', color: 'text-chart-4', bgColor: 'bg-chart-4/10' },
  ];

  const toggleTask = (taskId: string) => {
    setCompletedTasks((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  const currentPhase = houseGoalPhases.find((phase) => !phase.locked) || houseGoalPhases[0];
  const completedPhases = houseGoalPhases.filter((phase) => phase.locked === false && phase.phase < currentPhase.phase).length;
  const progress = (completedPhases / houseGoalPhases.length) * 100;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Link to={ROUTE_PATHS.DASHBOARD}>
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold">Goal Roadmap Module</h1>
              <p className="text-muted-foreground mt-1">Step-by-step plans to achieve your financial goals</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Goal Selector */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {goals.map((goal) => {
            const Icon = goal.icon;
            return (
              <motion.div
                key={goal.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.05 }}
                transition={springPresets.snappy}
              >
                <Card
                  className={`p-6 cursor-pointer transition-all ${
                    selectedGoal === goal.id ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                  }`}
                  onClick={() => setSelectedGoal(goal.id)}
                >
                  <div className={`w-12 h-12 rounded-full ${goal.bgColor} flex items-center justify-center mb-3`}>
                    <Icon className={`w-6 h-6 ${goal.color}`} />
                  </div>
                  <h4 className="font-semibold">{goal.label}</h4>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Active Goal Overview */}
        <Card className="p-6 mb-8 bg-primary/5 border-primary/20">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-2xl font-bold mb-1">🏠 Buy a House</h3>
              <p className="text-sm text-muted-foreground">Target: July 2026 • FHA Loan • $350k budget</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary font-mono">{Math.round(progress)}%</div>
              <div className="text-xs text-muted-foreground mt-1">Complete</div>
            </div>
          </div>
          <Progress value={progress} className="h-3 mb-4" />
          <div className="flex items-center gap-6 text-sm">
            <div>
              <span className="text-muted-foreground">Current Phase:</span>
              <span className="font-semibold ml-2">{currentPhase.title}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Phases Complete:</span>
              <span className="font-semibold ml-2">
                {completedPhases} of {houseGoalPhases.length}
              </span>
            </div>
          </div>
        </Card>

        {/* Phase-by-Phase Roadmap */}
        <div className="space-y-6">
          {houseGoalPhases.map((phase, index) => (
            <motion.div
              key={phase.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: index * 0.1 }}
            >
              <Card
                className={`p-6 ${
                  phase.locked
                    ? 'opacity-60'
                    : phase.phase === currentPhase.phase
                    ? 'border-primary bg-primary/5'
                    : 'border-border'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Phase Number */}
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      phase.locked
                        ? 'bg-muted'
                        : phase.phase === currentPhase.phase
                        ? 'bg-primary text-background'
                        : 'bg-primary/20 text-primary'
                    }`}
                  >
                    {phase.locked ? <Lock className="w-5 h-5" /> : <span className="font-bold">{phase.phase}</span>}
                  </div>

                  {/* Phase Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <h4 className="text-xl font-semibold">{phase.title}</h4>
                      {phase.phase === currentPhase.phase && (
                        <Badge variant="outline" className="border-primary text-primary">
                          Current Phase
                        </Badge>
                      )}
                      {phase.locked && (
                        <Badge variant="outline" className="border-muted-foreground text-muted-foreground">
                          Locked
                        </Badge>
                      )}
                    </div>

                    {/* Tasks */}
                    <div className="space-y-3 mb-4">
                      {phase.tasks.map((task) => (
                        <div
                          key={task.id}
                          className={`flex items-start gap-3 p-3 rounded-lg ${
                            completedTasks.includes(task.id) ? 'bg-primary/10' : 'bg-muted/30'
                          }`}
                        >
                          <Checkbox
                            checked={completedTasks.includes(task.id)}
                            onCheckedChange={() => toggleTask(task.id)}
                            disabled={phase.locked}
                          />
                          <span
                            className={`text-sm flex-1 ${
                              completedTasks.includes(task.id) ? 'line-through text-muted-foreground' : ''
                            }`}
                          >
                            {task.text}
                          </span>
                          {completedTasks.includes(task.id) && <CheckCircle className="w-4 h-4 text-primary" />}
                        </div>
                      ))}
                    </div>

                    {/* Pro Tip */}
                    <div className="p-3 bg-accent/10 border border-accent/20 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Lightbulb className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-xs font-semibold text-accent">Pro Tip:</span>
                          <p className="text-xs text-muted-foreground mt-1">{phase.proTip}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Add Goal Button */}
        <div className="mt-8 text-center">
          <Button variant="outline" size="lg">
            + Add New Goal
          </Button>
        </div>
      </div>
    </div>
  );
}
